import { Injectable, computed, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { filter } from 'rxjs/operators';

import { BreadcrumbItem, MenuItem } from 'app/shared/models';

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
    private readonly menuItems = signal<MenuItem[]>([]);
    private readonly currentUrl = signal<string>('/');
    private readonly dynamicItems = signal<BreadcrumbItem[]>([]);

    readonly breadcrumbs = computed<BreadcrumbItem[]>(() => {
        const url = this.currentUrl();
        const menu = this.menuItems();
        const dynamicItems = this.dynamicItems();

        const cleanUrl = url.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';

        const exact = menu.find(m => this.normalize(m.route) === cleanUrl);
        const match =
            exact ??
            menu
                .slice()
                .sort((a, b) => b.route.length - a.route.length)
                .find(m => cleanUrl.startsWith(this.normalize(m.route)));

        const items: BreadcrumbItem[] = [
            {
                label: 'components.sidebar.dashboard.label',
                route: '/dashboard',
                iconClass: 'fa-solid fa-house',
                translate: true,
            }
        ];

        if (!match || this.normalize(match.route) === '/dashboard') {
            items[0].route = undefined;
            return items;
        }

        items.push({
            label: match.label,
            route: dynamicItems.length > 0 ? match.route : undefined,
            iconClass: match.iconClass,
            translate: true,
        });

        items.push(...dynamicItems);

        return items;
    });

    constructor(router: Router) {
        router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(e => {
                this.currentUrl.set(e.urlAfterRedirects);
                this.dynamicItems.set([]);
            });

        this.currentUrl.set(router.url);
    }

    setMenu(items: MenuItem[]): void {
        this.menuItems.set(items ?? []);
    }

    setDynamic(items: BreadcrumbItem[]): void {
        this.dynamicItems.set(items ?? []);
    }

    clearDynamic(): void {
        this.dynamicItems.set([]);
    }

    private normalize(route: string): string {
        return (route ?? '/').replace(/\/$/, '') || '/';
    }
}
